import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  UtensilsCrossed, 
  ClipboardList, 
  Settings, 
  LogOut,
  Menu,
  X,
  Star,
  BarChart3,
  Clock,
  MessageCircle,
  Image,
  Wallet,
  Gift,
  Building2,
  Receipt,
  CreditCard
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { TutorialProvider, TutorialHelpButton } from './TutorialSystem';
import { EstablishmentSelector } from './restaurant/EstablishmentSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { path: '/restaurant-dashboard', label: 'Tableau de bord', icon: LayoutDashboard, tutorialId: 'nav-dashboard' },
  { path: '/restaurant-dashboard/orders', label: 'Commandes', icon: ClipboardList, tutorialId: 'nav-orders' },
  { path: '/restaurant-dashboard/menu', label: 'Menu', icon: UtensilsCrossed, tutorialId: 'nav-menu-link' },
  { path: '/restaurant/gallery', label: 'Photos', icon: Image, tutorialId: 'nav-gallery' },
  { path: '/restaurant-dashboard/hours', label: 'Horaires', icon: Clock, tutorialId: 'nav-hours' },
  { path: '/restaurant/deposits', label: 'Acomptes', icon: Wallet, tutorialId: 'nav-deposits' },
  { path: '/restaurant/promotions', label: 'Promotions & Fidélité', icon: Gift, tutorialId: 'nav-promotions' },
  { path: '/restaurant-dashboard/analytics', label: 'Analytics', icon: BarChart3, tutorialId: 'nav-analytics' },
  { path: '/restaurant-dashboard/reviews', label: 'Avis clients', icon: Star, tutorialId: 'nav-reviews' },
  { path: '/restaurant/chat', label: 'Chat Admin', icon: MessageCircle, showBadge: true, tutorialId: 'nav-chat' },
  { path: '/restaurant/multi-site', label: 'Multi-établissements', icon: Building2, tutorialId: 'nav-multisite' },
  { path: '/restaurant/referral', label: 'Parrainage', icon: Gift, tutorialId: 'nav-referral' },
  { path: '/restaurant/commissions', label: 'Commissions', icon: Receipt, tutorialId: 'nav-commissions' },
  { path: '/restaurant/stripe-connect', label: 'Paiements Stripe', icon: CreditCard, tutorialId: 'nav-stripe' },
  { path: '/restaurant-dashboard/settings', label: 'Paramètres', icon: Settings, tutorialId: 'nav-settings' },
];

// Also support /restaurant/* paths
const getActivePath = (pathname) => {
  // Map /restaurant/* to /restaurant-dashboard/*
  if (pathname.startsWith('/restaurant/')) {
    return pathname.replace('/restaurant/', '/restaurant-dashboard/');
  }
  if (pathname === '/restaurant') {
    return '/restaurant-dashboard';
  }
  return pathname;
};

export const DashboardSidebar = ({ className = '' }) => {
  const location = useLocation();
  const { logout, user, token } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread chat messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/chat/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className={`bg-white border-r border-gray-200 w-52 min-h-screen flex flex-col ${className}`}>
      {/* Logo */}
      <div className="p-4 border-b border-gray-100">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="LEMO" className="w-16 h-16 -my-2 object-contain" />
        </Link>
      </div>

      {/* User Info */}
      <div className="p-3 border-b border-gray-100">
        <p className="text-xs text-gray-500">Connecté en tant que</p>
        <p className="font-semibold text-gray-900 truncate text-sm">{user?.name}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto" data-tutorial="nav-menu">
        <ul className="space-y-0.5">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-tutorial={item.tutorialId}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive 
                      ? 'bg-emerald-50 text-emerald-600' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="truncate">{item.label}</span>
                  {item.showBadge && unreadCount > 0 && (
                    <span className="absolute right-2 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-gray-100">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </button>
      </div>
    </aside>
  );
};

export const DashboardLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <TutorialProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Sidebar */}
        <DashboardSidebar className="hidden lg:flex fixed left-0 top-0 h-full" />

        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 h-16">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>
            <EstablishmentSelector />
            <TutorialHelpButton />
          </div>
        </header>

        {/* Desktop Header with Establishment Selector */}
        <div className="hidden lg:flex fixed top-0 left-52 right-0 h-16 bg-white border-b border-gray-100 z-30 items-center justify-between px-6">
          <EstablishmentSelector />
          <TutorialHelpButton />
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white animate-slide-in-right">
              <button
                onClick={() => setSidebarOpen(false)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg"
              >
              <X className="w-5 h-5" />
            </button>
            <DashboardSidebar />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-52 min-h-screen lg:pt-16">
        <div className="p-4 lg:p-8">
          {title && (
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">{title}</h1>
          )}
          {children}
        </div>
      </main>
    </div>
    </TutorialProvider>
  );
};
